'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { useLocale } from 'next-intl';
import { Icon } from '@/components/icons/svg-icons';
import { AutoPopupModal } from '@/components/widgets/modal';
import { Button } from '@nf/ui';
import { convertCultureToRef } from '@/utils/common-util';
import { useAccountInfo } from '@/hooks/query/account';
import { useLicSettings } from '@/domains/config/hooks/use-lic-settings';
import { useTranslations } from 'next-intl';
import { useAuthToken } from '@/store/auth';
import { useAppearanceStore } from '@/store/appearance';
import { ViewEnum } from '@/types';
import webStorageKeys from '@/constants/web-storage-keys';
import {
	isWithinVipPopupWindow,
	shouldShowVipPopupByCooldown,
} from './wc-promotion-vip-modal-schedule';
import { isWcVipProgramBaseEligible } from './wc-promotion-vip-program-eligibility';
import styles from './wc-promotion-vip-modal.module.scss';
import { CdnImg } from '@/features/cdn-img';
import { AutoPopupKey } from '@nf/auto-popup-handler';

const supportLanguages = ['ch', 'cs', 'zhcn', 'jp', 'ko', 'th', 'id', 'beng', 'tr', 'vn'];

const getVipModalStorageKey = (view: ViewEnum) =>
	`${webStorageKeys.WC_PROMOTION_VIP_MODAL_LAST_DISMISSED_AT_PREFIX}:${ViewEnum[view]}`;

export const WcPromotionVipModal = () => {
	const t = useTranslations('General');
	const locale = useLocale();
	const shortLang = convertCultureToRef(locale ?? 'en-US', true);
	const lang = supportLanguages.includes(shortLang) ? shortLang === 'zhcn' ? 'cs' : shortLang : 'en';

	const authToken = useAuthToken();
	const view = useAppearanceStore(state => state.view);
	const storageKey = useMemo(() => getVipModalStorageKey(view), [view]);

	const { user } = useAccountInfo();
	const { enableVipProgramHint: isEnableVipProgramHint } = useLicSettings();

	const baseEligible =
		isWcVipProgramBaseEligible({
			authToken,
			enableVipProgramHint: isEnableVipProgramHint,
			user,
		}) && isWithinVipPopupWindow(Date.now());

	const [evaluated, setEvaluated] = useState(false);
	const [isOpen, setIsOpen] = useState(false);

	useEffect(() => {
		if (!baseEligible) {
			setEvaluated(false);
			setIsOpen(false);
			return;
		}

		const raw = typeof window !== 'undefined' ? localStorage.getItem(storageKey) : null;
		const parsed = raw !== null ? Number(raw) : NaN;
		const lastDismissedAtMs = !Number.isNaN(parsed) ? parsed : null;
		const show = shouldShowVipPopupByCooldown(lastDismissedAtMs, Date.now());

		setIsOpen(show);
		setEvaluated(true);
	}, [baseEligible, storageKey]);

	const persistDismiss = useCallback(() => {
		const now = Date.now();
		if (typeof window !== 'undefined') {
			localStorage.setItem(storageKey, String(now));
		}
		setIsOpen(false);
		setEvaluated(true);
	}, [storageKey]);

	if (!baseEligible || !evaluated || !isOpen) {
		return null;
	}

	return (
		<AutoPopupModal
			popupKey={AutoPopupKey.WcPromotionVip}
			isOpen={isOpen}
			variant="event"
			closeModal={persistDismiss}
			showHeader={false}
			className={styles.promoModal}
			isShowClose={true}
			isBackdropClose={false}
		>
			<div className={styles.container}>
				<Button variant="icon" className={clsx(styles.closeButton)} onClick={persistDismiss}>
					<Icon name="close-round-line" />
				</Button>
				<div className={styles.vip}>
					<CdnImg
						src={`/images/promotion/worldcup2026/vip-modal/kv-vip_${lang}.png`}
						alt="World Cup 2026 VIP"
						isGalaxyFolder={true}
						width="360"
						height="348"
					/>
					<button
						className={styles.button}
						onClick={persistDismiss}
					>
						{t('lbl_VIPProgramPopup04')}
					</button>
				</div>
			</div>
		</AutoPopupModal>
	);
};
